import { createSelector } from 'reselect'
import {
  getTourV2IdForCheckoutItem,
  isLuxPlusSubscriptionItem,
  isTourV2ExperienceItem,
  isSubscriptionJoinItem,
  isTourV2Item, isInsuranceItem,
  findPostPurchaseCheckout,
  isBedbankItem,
  isLEHotelItem,
} from 'lib/checkout/checkoutUtils'
import { getAllOffers } from 'selectors/offerSelectors'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import isPaymentScheduleEnabled from './isPaymentScheduleEnabled'

const isItemAllowedWithPaymentSchedules = (item: App.Checkout.AnyItem): boolean => {
  return isTourV2Item(item) ||
    isTourV2ExperienceItem(item) ||
    isLuxPlusSubscriptionItem(item) ||
    isSubscriptionJoinItem(item) ||
    isInsuranceItem(item) ||
    isBedbankItem(item) ||
    isLEHotelItem(item)
}

const canGetItemsPaymentScheduleInfo = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getAllOffers(state),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => state.offer.bedbankOfferRates,
  (items, offers, isGift, bedbankRatesByOffer): Array<boolean> | false => {
    if (isGift || (!items.every(isItemAllowedWithPaymentSchedules))) {
      return false
    }

    const tourV2Items = items.filter(isTourV2Item)
    const bedbankItems = items.filter(isBedbankItem)
    const hotelItems = items.filter(isLEHotelItem)

    // Payment schedules for bedbank items are only supported when there is exactly one bedbank item and no tour items
    const hasOnlyOneBedbankItem = bedbankItems.length === 1
    const hasNoTourItems = tourV2Items.length === 0
    const hasNoHotelItems = hotelItems.length === 0
    if (hasNoTourItems && !hasOnlyOneBedbankItem && hasNoHotelItems) {
      return false
    }

    if (hotelItems.length > 1) {
      return false
    }

    return [...tourV2Items, ...bedbankItems, ...hotelItems].map((item) => {
      if (isTourV2Item(item) || (isBedbankItem(item) && tourV2Items.length > 0)) {
        const tourItem = isTourV2Item(item) ? item : tourV2Items[0]
        const tourV2Offer = offers[getTourV2IdForCheckoutItem(tourItem)] as App.Tours.TourV2Offer | undefined
        const templateId = tourV2Offer?.paymentScheduleInfo?.templateId
        return !!templateId
      } else if (isBedbankItem(item)) {
        const rateKey = getBedbankRateKey([item.occupancy], item.checkIn, item.checkOut)
        const bedbankOfferRates = bedbankRatesByOffer[item.offerId]?.[rateKey]
        const bedbankRate = bedbankOfferRates?.find(rate => rate.id === item.roomRateId)
        return !!bedbankRate?.paymentScheduleInfo?.templateId
      } else if (isLEHotelItem(item)) {
        const hotelOffer = offers[item.offerId] as App.HotelOffer | undefined
        return !!hotelOffer?.paymentScheduleInfo?.templateId
      }
      return false
    })
  },
)

const isAnyItemDisabledForPaymentSchedule = createSelector(
  canGetItemsPaymentScheduleInfo,
  (itemsPaymentScheduleInfo): boolean => !itemsPaymentScheduleInfo || itemsPaymentScheduleInfo.some(item => !item),
)

const isPaymentScheduleAvailable = createSelector(
  (state: App.State) => isPaymentScheduleEnabled(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => isAnyItemDisabledForPaymentSchedule(state),
  (
    isPaymentScheduleEnabled,
    postPurchase,
    isAnyItemDisabledForPaymentSchedule,
  ): boolean => {
    const result = (
      isPaymentScheduleEnabled &&
      !postPurchase &&
      !isAnyItemDisabledForPaymentSchedule
    )
    return result
  },
)

export default isPaymentScheduleAvailable
